import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react'
import { ExternalToast, toast as sonnerToast } from 'sonner'
import Button from './button'
import { getApiErrorMessage } from '@/shared/utils/api-error'
import {
  safeRelativeRedirect,
  toRouterTarget,
} from '@/shared/utils/route-utils/redirect-utils'

/**
 * Auto-dismiss delay of every app toast (#1516 anomaly 10). Stated here instead
 * of relying on the `<Toaster/>` default so a toast can never outlive the action
 * it reports: a toast that stays sits on top of the document footer action bar
 * and eats the clicks meant for « Valider et envoyer la facture ». Sonner pauses
 * this timer while the pointer rests on the toast — which is exactly what
 * happens when the operator reaches for the button underneath — so every toast
 * also carries a close button. Pass `duration: Infinity` to opt out (the
 * stale-version prompt in `main.tsx` does).
 */
export const TOAST_DURATION_MS = 5000

/** Shown when a caller reports an outcome without wording it. */
export const TOAST_FALLBACK_SUCCESS = 'Opération réussie'
export const TOAST_FALLBACK_ERROR =
  "Désolé, une erreur s'est produite, réessayez plus tard"

/**
 * Sonner sizes an unstyled `toast.custom` to its content, so short and long
 * toasts stacked at different widths. `w-full` on the `<li>` pins every one to
 * the toaster's `--width`; below 600px sonner's own higher-specificity rule
 * takes over and the toast goes edge-to-edge.
 */
const TOAST_WIDTH_CLASS = 'w-full'

/** Sonner's own surface: neutral card, one hairline border, type in the icon. */
const TOAST_SURFACE_CLASS =
  'flex items-start gap-3 rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg'

/**
 * Bounded and scrollable rather than ellipsised: an `error` sentence and a 400
 * `ValidationPipe` list both ride in the TITLE and #1487 rejected swallowing
 * them, and an ellipsis is unrecoverable on touch. On the MESSAGE only —
 * capping the row would scroll « Fermer » out of reach (#1665).
 */
const TOAST_MESSAGE_CLASS = 'min-w-0 flex-1 max-h-28 overflow-y-auto'

/**
 * A `shrink-0` action with a wide label starves the message column. Fixed cap,
 * not a percentage: a percentage is ignored while sonner measures intrinsic
 * width and the toast then overflows its own column.
 */
const TOAST_ACTION_CLASS = 'max-w-[8rem] shrink-0'

/** `to` is a same-origin relative path; an off-origin one costs the button. */
type ToastAction = {
  label: string
  onClick?: () => void
  to?: string
}

const resolveAction = (action: ToastAction) => {
  if (action.onClick) return action.onClick

  const target = safeRelativeRedirect(action.to)
  if (!target) return null

  // Imported at click time: `@/router` pulls the route tree back through here.
  return () =>
    void import('@/router').then(({ router }) =>
      router.navigate(toRouterTarget(target))
    )
}

type ToastProps = {
  title: string
  description?: string
  action?: ToastAction
  options?: ExternalToast | undefined
}

type ToastType = 'info' | 'success' | 'warning' | 'error'

/**
 * One treatment for all four types: same surface, same spacing, the type shows
 * in the icon. Colour is never the only carrier — the glyph differs per type,
 * and `label` names it for a screen reader (sonner announces the toast through
 * an `aria-live` region that would otherwise read the sentence with no type).
 */
const toastConfigs = {
  info: {
    Icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-400',
    label: 'Information',
  },
  success: {
    Icon: CircleCheck,
    iconColor: 'text-green-600 dark:text-green-400',
    label: 'Succès',
  },
  warning: {
    Icon: TriangleAlert,
    iconColor: 'text-amber-600 dark:text-amber-400',
    label: 'Avertissement',
  },
  error: {
    Icon: CircleAlert,
    iconColor: 'text-destructive',
    label: 'Erreur',
  },
} as const

const createCustomToast = (toastType: ToastType, props: ToastProps) => {
  const { Icon, iconColor, label } = toastConfigs[toastType]
  const { title, description, action, options } = props
  const onAction = action ? resolveAction(action) : null

  return sonnerToast.custom(
    (id) => (
      <div className={TOAST_SURFACE_CLASS}>
        <Icon
          aria-hidden='true'
          className={`mt-0.5 size-4 shrink-0 ${iconColor}`}
        />
        <div className={TOAST_MESSAGE_CLASS}>
          {/* `whitespace-pre-wrap`: a 400 list reaches the title `\n`-joined. */}
          <div className='text-sm font-medium whitespace-pre-wrap'>
            <span className='sr-only'>{label} : </span>
            {title}
          </div>
          {description && (
            <div className='mt-1 text-sm whitespace-pre-wrap text-muted-foreground'>
              {description}
            </div>
          )}
        </div>
        {action && onAction && (
          <Button
            size='sm'
            className={TOAST_ACTION_CLASS}
            title={action.label}
            onClick={() => {
              // A toast surviving its own action click is #1516 anomaly 10 again.
              sonnerToast.dismiss(id)
              onAction()
            }}
          >
            {/* The button centres its content, so clipping it clips both ends. */}
            <span className='min-w-0 truncate'>{action.label}</span>
          </Button>
        )}
        <button
          type='button'
          aria-label='Fermer'
          onClick={() => sonnerToast.dismiss(id)}
          className='shrink-0 cursor-pointer rounded-sm p-1 text-muted-foreground opacity-70 outline-none hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-safe:transition-opacity'
        >
          <X className='size-4' />
        </button>
      </div>
    ),
    {
      ...options,
      className: TOAST_WIDTH_CLASS,
      duration: options?.duration ?? TOAST_DURATION_MS,
    }
  )
}

type ToastOptions = {
  action?: ToastAction
  duration?: number
}

const emit = (
  toastType: ToastType,
  title: string,
  description?: string,
  options?: ToastOptions
) =>
  createCustomToast(toastType, {
    title,
    description,
    action: options?.action,
    options: { duration: options?.duration },
  })

/**
 * The single entry point for every transient Harmony toast (#1665). Persisted
 * business notifications are a different thing — `hooks/user-notifications`.
 */
export const toast = {
  info: (title: string, description?: string, options?: ToastOptions) =>
    emit('info', title, description, options),

  warning: (title: string, description?: string, options?: ToastOptions) =>
    emit('warning', title, description, options),

  success: (title?: string, description?: string, options?: ToastOptions) =>
    emit('success', title || TOAST_FALLBACK_SUCCESS, description, options),

  error: (title?: string, description?: string, options?: ToastOptions) =>
    emit('error', title || TOAST_FALLBACK_ERROR, description, options),

  /** Turns an Axios failure into its French sentence, then toasts it. */
  apiError: (error: unknown, fallbackMessage?: string) =>
    toast.error(getApiErrorMessage(error, fallbackMessage)),

  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
}

export default toast
